import { useEffect, useMemo } from 'react';

import { QueryHookOptions, useLazyQuery } from '@apollo/react-hooks';
import { isNil } from 'lodash-es';

import { useGetOrderLazyQuery } from 'generated/graphql-gateway';
import { DeliveryStatus } from 'generated/rbi-graphql';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import {
  GetOrderQuery,
  IGetOrderQueryResponse,
  IGetOrderQueryVariables,
} from 'remote/queries/order';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderStatus } from 'state/order';
import { orderPollFailure, orderPollSuccessful } from 'state/order/order-state-utils';
import noop from 'utils/noop';

import { handleOrderStatusChange } from './on-order-status-change';
import { IUseOrderStatusParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

export const useOrderStatus = ({
  failureStatuses,
  onSuccess = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  successStatuses,
  skip,
}: IUseOrderStatusParams) => {
  const queryParams: QueryHookOptions<IGetOrderQueryResponse, IGetOrderQueryVariables> = {
    pollInterval: orderStatusPollInterval,
  };
  const isFulfillmentServiceEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_FULFILLMENT_SERVICE_GET_ORDER
  );
  const enableWebCatering = useFlag(LaunchDarklyFlag.ENABLE_WEB_CATERING);
  const skipQuery = skip || isNil(rbiOrderId) || isFulfillmentServiceEnabled;
  const skipFulfillmentQuery = skip || isNil(rbiOrderId) || !isFulfillmentServiceEnabled;

  if (rbiOrderId) {
    queryParams.variables = { rbiOrderId };
  }

  const [getOrderFulfillment, lazyQueryResultFulfillment] = useGetOrderLazyQuery({
    variables: queryParams.variables,
    pollInterval: orderStatusPollInterval,
  });
  const [getOrder, lazyQueryResult] = useLazyQuery<IGetOrderQueryResponse, IGetOrderQueryVariables>(
    GetOrderQuery,
    queryParams
  );
  const { called, stopPolling, data } = lazyQueryResult;
  const {
    stopPolling: stopPollingFulfillment,
    data: dataFulfillment,
    called: calledFulfillment,
  } = lazyQueryResultFulfillment;

  if (!skipQuery) {
    if (!called) {
      getOrder();
    }
  } else if (!skipFulfillmentQuery) {
    if (!calledFulfillment) {
      getOrderFulfillment();
    }
  }

  // True when a query should fire but useLazyQuery hasn't acknowledged the call yet.
  // useLazyQuery sets called/loading on the *next* render, so without this guard
  // consumers see loading=false + serverOrder=null on the very first render.
  const isQueryPending = (!skipQuery && !called) || (!skipFulfillmentQuery && !calledFulfillment);

  const serverOrder = useMemo<any | null>(() => {
    if (called) {
      return data?.order ?? null;
    }
    if (calledFulfillment) {
      return dataFulfillment?.order ?? null;
    }
    return null;
  }, [data, dataFulfillment, called, calledFulfillment]);

  const orderStatus = serverOrder && serverOrder.status;
  const orderDeliveryStatus: DeliveryStatus | null =
    serverOrder && serverOrder.delivery && serverOrder.delivery.status;

  const prevOrderStatus = usePrevious<OrderStatus | null>(orderStatus);
  const prevOrderDeliveryStatus = usePrevious<DeliveryStatus | null>(orderDeliveryStatus);

  const failure =
    !!serverOrder &&
    serverOrder.rbiOrderId === rbiOrderId &&
    orderPollFailure({
      cateringFailureStatus: enableWebCatering ? failureStatuses.pos : failureStatuses.catering,
      deliveryFailureStatus: failureStatuses.delivery,
      order: serverOrder,
      orderFailureStatus: failureStatuses.pos,
    });

  // update with catering
  const success =
    !!serverOrder &&
    serverOrder.rbiOrderId === rbiOrderId &&
    orderPollSuccessful({
      cateringSuccessStatus: enableWebCatering ? successStatuses.pos : successStatuses.catering,
      deliverySuccessStatus: successStatuses.delivery,
      order: serverOrder,
      orderSuccessStatus: successStatuses.pos,
    });

  useEffect(() => {
    if (skipQuery || success || failure) {
      if (called) {
        stopPolling();
      }
    }
    if (skipFulfillmentQuery || success || failure) {
      if (calledFulfillment) {
        stopPollingFulfillment();
      }
    }
  }, [
    skipQuery,
    skipFulfillmentQuery,
    success,
    failure,
    stopPolling,
    stopPollingFulfillment,
    called,
    calledFulfillment,
  ]);

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }

    handleOrderStatusChange({
      failure,
      onSuccess,
      orderStatus,
      orderDeliveryStatus,
      prevOrderStatus,
      prevOrderDeliveryStatus,
      serverOrder,
      success,
    });
  }, [
    failure,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    success,
  ]);

  useEffectOnUnmount(() => {
    if (called) {
      stopPolling();
    }
    if (calledFulfillment) {
      stopPollingFulfillment();
    }
  });

  const baseResult = skipFulfillmentQuery ? lazyQueryResult : lazyQueryResultFulfillment;

  return {
    ...baseResult,
    loading: isQueryPending || baseResult.loading,
    failure,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    success,
  };
};
